import React from 'react'
import { Container, Image } from 'react-bootstrap'
import { HiOutlineMail, HiOutlinePhone } from 'react-icons/hi'
import BreatheEV_Logo from '../../Images/logo.png'
import styles from './SupportFAQ.module.css'

const SUPPORT_EMAIL = 'info@breathe-ev.com';
const SUPPORT_PHONE_DISPLAY = '+1 (877) 367-1001';
const SUPPORT_PHONE_TEL = '+18773671001';

const faqs = [
  {
    question: 'What connector or adapter do I need?',
    answer: 'Most stations use a J1772 connector. Many newer EVs need an adapter unless the vehicle already supports J1772 natively, so check your vehicle’s charging port type before your visit.'
  },
  {
    question: 'How much does charging cost?',
    answer: 'Pricing is pay-per-use and varies by site, typically billed per kilowatt-hour. Actual charging speed and cost depend on your vehicle’s onboard charging capacity, not just the station’s rating.'
  },
  {
    question: 'What are "idle fees" and how can I avoid them?',
    answer: 'Idle fees may apply if your vehicle stays plugged in after the grace period ends. The grace period gives you a short window after your charging session completes to unplug before fees start accruing; once that window passes and the car is still connected, idle fees begin automatically.'
  },
  {
    question: 'Can I extend my reservation or charging slot before the grace period ends?',
    answer: 'Yes. You can extend your reservation or charging slot through the app any time before the grace period starts. Once the grace period has started, an extension is no longer available and idle fees will apply if the vehicle is still plugged in after grace period.'
  },
  {
    question: 'How do I find a charger near me?',
    answer: 'Open the app and use the Map View. Enable location services so the map centers on you, or use the search bar to look up an area or address. If you can\'t find a nearby location, make sure location services are on, and if the site is private you may need to request access from the property management team.'
  },
  {
    question: 'Can I see if a charger is available before I arrive?',
    answer: 'Yes. BreatheEV shows live station availability in the app. For reservation-enabled sites, you can open a location and choose a time to charge to see open time slots on the timeline and book one in advance.'
  },
  {
    question: 'Why can\'t I find a charger that I know exists?',
    answer: 'A few common reasons: location services may be off, the map may be zoomed away from the site, or the location may be private/restricted so it doesn\'t appear publicly. Try enabling location, searching the address, or asking the property to grant access. If it still doesn\'t appear, contact support and we\'ll help.'
  },
  {
    question: 'How do I start a charging session?',
    answer: 'Plug the connector into your vehicle, then open your booking page in the app and press the blinking green Play button. If the button isn\'t blinking after you plug in, tap Refresh. At instant-start sites you simply select the available charger and press Start.'
  },
  {
    question: 'Can I restart a charging session?',
    answer: 'Yes. Reconnect the connector and press the green Play button again (use Refresh if it isn\'t blinking). If you\'re within a reserved window and time remains, you can resume; if your reservation has ended, you may need to book a new slot.'
  },
  {
    question: 'Why won\'t my charging session start?',
    answer: 'The most common cause is that the connector isn\'t fully seated in your vehicle, or the green Play button hasn\'t refreshed yet. Make sure the cable is firmly plugged in, then press Refresh so the Play button illuminates, and press it to begin. If it still won\'t start, contact support — we can often initiate the charge remotely.'
  },
  {
    question: 'The charging speed is slower than expected.',
    answer: 'Charging speed depends on your vehicle, its current battery level, temperature, and how many vehicles are sharing a station. If the speed seems far below what the station should deliver, let support know the location and charger number.'
  },
  {
    question: 'How am I charged?',
    answer: 'BreatheEV runs on a wallet model. You add funds to your BreatheEV Wallet by card or Apple Pay, and session costs (plus any applicable fees) are drawn from that balance. All payments are processed securely through Stripe.'
  },
  {
    question: 'Why was I charged more than expected?',
    answer: 'Common reasons include a longer reserved duration than you used, an idle fee for staying plugged in past your session, or a no-show/unutilized-time fee on a reservation. Every applicable rate and fee is shown in the app before you book. If a charge still looks wrong, contact support and we\'ll review it.'
  },
  {
    question: 'Are chargers open to the public?',
    answer: 'Some BreatheEV locations are public and some are private or restricted to a property\'s residents, tenants, or guests. Public sites appear on the map to everyone; for private sites you may need an access link or approval from the property management.'
  },
  {
    question: 'Can I receive alerts when charging is complete?',
    answer: 'Yes. BreatheEV sends reminders around your session, including before your reserved time ends, so you know when to return and move your vehicle. Make sure notifications are turned on to receive them.'
  }
];

const SupportFAQ = () => {
  return (
    <div className={styles.page}>
      <Container>
        <div className={styles.hero}>
          <Image src={BreatheEV_Logo} className={styles.logo} alt="BreatheEV" />
          <div className={styles.heroText}>
            <h2 className={styles.title}>Support</h2>
            <p className={styles.subtitle}>Get in touch with our team, or find quick answers below.</p>
          </div>
        </div>

        <div className={styles.card}>
          <h3>Contact Us</h3>
          <div className={styles.contactRow}>
            <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.contactItem}>
              <span className={styles.contactIcon}><HiOutlineMail /></span>
              <span>
                <span className={styles.contactLabel}>Email</span>
                <span className={styles.contactValue}>{SUPPORT_EMAIL}</span>
              </span>
            </a>
            <a href={`tel:${SUPPORT_PHONE_TEL}`} className={styles.contactItem}>
              <span className={styles.contactIcon}><HiOutlinePhone /></span>
              <span>
                <span className={styles.contactLabel}>Phone</span>
                <span className={styles.contactValue}>{SUPPORT_PHONE_DISPLAY}</span>
              </span>
            </a>
          </div>
        </div>

        <div className={styles.card}>
          <h3>Frequently Asked Questions</h3>
          <div className={styles.faqList}>
            {faqs.map((faq, idx) => (
              <details key={idx} className={styles.faqItem}>
                <summary className={styles.faqQuestion}>
                  <span className={styles.faqNumber}>{idx + 1}</span>
                  <span className={styles.faqQuestionText}>{faq.question}</span>
                  <span className={styles.faqIcon} aria-hidden="true"></span>
                </summary>
                <p className={styles.faqAnswer}>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </Container>
    </div>
  );
}

export default SupportFAQ
